import React, { useCallback } from "react";
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { forgetLegacyOidcState, Permission, UserData, UserProfile } from "./auth";
import { useTranslationsData } from "../components/TranslationsProvider";
import { FetchFunction, useFetch } from "../components/FetchProvider";

const UserDataContext = createContext(undefined);

type ServerUserData = {
    user_id: number;
    account_page: string;
    permissions: Permission[];
    profile: UserProfile;
};

/**
 * Fetches who is logged in. Null when nobody is: on a login page that is expected, anywhere else FetchProvider has
 * already sent the browser to the login by the time this returns.
 */
async function fetchUserData(fetch: FetchFunction): Promise<UserData> {
    const response: Response = await fetch(window.location.origin + "/api/user-data");

    if (response.status !== 200) {
        console.debug(`No user data (${response.status})`);
        return null;
    }

    const serverUserData = (await response.json()) as ServerUserData;
    return new UserData(
        serverUserData.user_id,
        serverUserData.account_page,
        serverUserData.profile,
        serverUserData.permissions,
    );
}

export function useUserData(): UserData {
    return useContext(UserDataContext).userData;
}

type OnUserDataChange = () => Promise<void>;

export function useOnUserDataChange(): OnUserDataChange {
    return useContext(UserDataContext).onUserDataChange;
}

/**
 * Context provider that fetches the roles from Heimdall.
 *
 * Use useAuth() to access the authorization information.
 */
export const UserDataProvider = ({ children }: { children: ReactNode }) => {
    const [userData, setUserData] = useState<UserData>(null);
    const { setUserLocale } = useTranslationsData();
    const fetch = useFetch();

    const fetchAndSetUserData = useCallback(
        async () =>
            fetchUserData(fetch)
                .then((data: UserData) => {
                    console.debug("User data arrived", data);
                    setUserData(data);
                    if (data === null) return;
                    setUserLocale(data.profile.locale);
                })
                .catch((e) => console.error("Failed to fetch user data", e)),
        [fetch, setUserLocale],
    );

    useEffect(() => {
        forgetLegacyOidcState();
        fetchAndSetUserData();
    }, [fetchAndSetUserData]);

    // The server reloads the grants itself; the profile is simply fetched again.
    const onUserDataChange = fetchAndSetUserData;

    return <UserDataContext.Provider value={{ userData, onUserDataChange }}>{children}</UserDataContext.Provider>;
};
